// forms/safetyAuditForm/actions.js

import { makePhotosActions } from "../photosForm/photosFormActions";
import { makeJsaActions } from "../jsaForm/jsaFormActions";
import { makeSafetyAuditAutosaveErrorsActions } from "./autosaveErrors/safetyAuditFormAutosaveErrorsActions";
import { filtersInitialState } from "../../shared/filters/filtersState";
import { getDelLists } from "../../../api/DelApi";
import {
  associateDelWithSafetyAudit,
  disassociateDelFromSafetyAudit,
  getSafetyAuditJsa,
} from "../../../api/SafetyFormApi";

const bump = (set, get, tabKey, patch = null) => {
  const s = get();
  const prev = s[tabKey] || {};
  const nextRev = (prev.rev || 0) + 1;
  const next = patch
    ? { [tabKey]: { ...prev, rev: nextRev, ...patch } }
    : { [tabKey]: { ...prev, rev: nextRev } };
  set(next);
};

export const makeSafetyAuditFormActionsForTab = (set, get, tabKey) => {
  const api = {
    // Helper to get the current safety audit from the tab
    getCurrentSafetyAudit: () => {
      const slice = get()[tabKey];
      if (!slice || !slice.currentSafetyAudit) {
        throw new Error(
          `No currentSafetyAudit found in ${tabKey}. Did you call startNewSafetyAudit() or fetchSafetyAudit()?`
        );
      }
      return slice.currentSafetyAudit;
    },

    // Bump revision for autosave tracking
    bump: (patch) => bump(set, get, tabKey, patch),

    // UI update helper for signature modal
    setSafetyAuditFormUI: (patch) => {
      const s = get();
      const prev = s[tabKey].safetyAuditForm.safetyAuditFormUI;
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      set({
        [tabKey]: {
          ...s[tabKey],
          safetyAuditForm: {
            ...s[tabKey].safetyAuditForm,
            safetyAuditFormUI: { ...prev, ...nextPatch },
          },
        },
      });
    },

    // UI update helper for photos
    setPhotosUi: (patch) => {
      const s = get();
      const prev = s[tabKey].safetyAuditForm.photosUI;
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      set({
        [tabKey]: {
          ...s[tabKey],
          safetyAuditForm: {
            ...s[tabKey].safetyAuditForm,
            photosUI: { ...prev, ...nextPatch },
          },
        },
      });
    },

    // Get photos UI state
    getPhotosUi: () => {
      return get()[tabKey].safetyAuditForm.photosUI;
    },
  };

  return Object.freeze({
    // Actions using the data class methods for the Safety Audit
    // Each action accepts an optional emitPatch callback for WebSocket autosave

    updateAuditDate(auditDate, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateAuditDate(auditDate);
      const patch = safetyAudit.toPatch({ auditDate: safetyAudit.audit_date });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateAuditTime(auditTime, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateAuditTime(auditTime);
      const patch = safetyAudit.toPatch({ auditTime: safetyAudit.audit_time });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateProjectNumber(projectInfo, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateProjectNumber(projectInfo);
      const patch = safetyAudit.toPatch({
        projectNumber: safetyAudit.project_number,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateSupervisor(supervisor, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateSupervisor(supervisor);
      const patch = safetyAudit.toPatch({ supervisor: safetyAudit.supervisor });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateScopeOfWork(scopeOfWork, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateScopeOfWork(scopeOfWork);
      const patch = safetyAudit.toPatch({
        scopeOfWork: safetyAudit.scope_of_work,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updatePwJsa(pwJsa, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updatePwJsa(pwJsa);
      const patch = safetyAudit.toPatch({ pwJsa: safetyAudit.pw_jsa });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updatePwFieldNotes(pwFieldNotes, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updatePwFieldNotes(pwFieldNotes);
      const patch = safetyAudit.toPatch({
        pwFieldNotes: safetyAudit.pw_field_notes,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updatePwComments(pwComments, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updatePwComments(pwComments);
      const patch = safetyAudit.toPatch({ pwComments: safetyAudit.pw_comments });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateJsaSigned(jsaSigned, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateJsaSigned(jsaSigned);
      const patch = safetyAudit.toPatch({ jsaSigned: safetyAudit.jsa_signed });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateEquipmentOnSite(equipmentOnSite, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateEquipmentOnSite(equipmentOnSite);
      const patch = safetyAudit.toPatch({
        equipmentOnSite: safetyAudit.equipment_on_site,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateHazardsPresent(hazardsPresent, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateHazardsPresent(hazardsPresent);
      const patch = safetyAudit.toPatch({
        hazardsPresent: safetyAudit.hazards_present,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateHazardsOnJsaYes(hazardsOnJsaYes, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateHazardsOnJsaYes(hazardsOnJsaYes);
      // When updating yes, also send the no value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        hazardsOnJsaYes: safetyAudit.hazards_on_jsa_yes,
        hazardsOnJsaNo: safetyAudit.hazards_on_jsa_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateHazardsOnJsaNo(hazardsOnJsaNo, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateHazardsOnJsaNo(hazardsOnJsaNo);
      // When updating no, also send the yes value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        hazardsOnJsaYes: safetyAudit.hazards_on_jsa_yes,
        hazardsOnJsaNo: safetyAudit.hazards_on_jsa_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateEngineeringControls(engineeringControls, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateEngineeringControls(engineeringControls);
      const patch = safetyAudit.toPatch({
        engineeringControls: safetyAudit.engineering_controls,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updatePpeUsed(ppeUsed, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updatePpeUsed(ppeUsed);
      const patch = safetyAudit.toPatch({ ppeUsed: safetyAudit.ppe_used });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateCorrectPpeYes(correctPpeYes, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateCorrectPpeYes(correctPpeYes);
      // When updating yes, also send the no value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        correctPpeYes: safetyAudit.correct_ppe_yes,
        correctPpeNo: safetyAudit.correct_ppe_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateCorrectPpeNo(correctPpeNo, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateCorrectPpeNo(correctPpeNo);
      // When updating no, also send the yes value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        correctPpeYes: safetyAudit.correct_ppe_yes,
        correctPpeNo: safetyAudit.correct_ppe_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateProperlyTrainedYes(properlyTrainedYes, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateProperlyTrainedYes(properlyTrainedYes);
      // When updating yes, also send the no value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        properlyTrainedYes: safetyAudit.properly_trained_yes,
        properlyTrainedNo: safetyAudit.properly_trained_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateProperlyTrainedNo(properlyTrainedNo, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateProperlyTrainedNo(properlyTrainedNo);
      // When updating no, also send the yes value since they're mutually exclusive
      const patch = safetyAudit.toPatch({
        properlyTrainedYes: safetyAudit.properly_trained_yes,
        properlyTrainedNo: safetyAudit.properly_trained_no,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateProperlyTrainedComments(properlyTrainedComments, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateProperlyTrainedComments(properlyTrainedComments);
      const patch = safetyAudit.toPatch({
        properlyTrainedComments: safetyAudit.properly_trained_comments,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateCertsObtained(certsObtained, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateCertsObtained(certsObtained);
      const patch = safetyAudit.toPatch({
        certsObtained: safetyAudit.certs_obtained,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updatePositiveFindings(positiveFindings, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updatePositiveFindings(positiveFindings);
      const patch = safetyAudit.toPatch({
        positiveFindings: safetyAudit.positive_findings,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    updateNegativeFindings(negativeFindings, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateNegativeFindings(negativeFindings);
      const patch = safetyAudit.toPatch({
        negativeFindings: safetyAudit.negative_findings,
      });
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    // Employee actions
    addNewBlankEmployee(emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.addNewBlankEmployee();
      // Get the newly added employee (last in the array)
      const newEmployee =
        safetyAudit.employees[safetyAudit.employees.length - 1];
      const patch = newEmployee.toPatch("add");
      if (emitPatch) emitPatch(patch);
      api.bump();
    },

    removeEmployee(removeEmployeeRowId, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      // Get the employee before removing to create the patch
      const employee = safetyAudit.employees.find(
        (emp) => emp.id === removeEmployeeRowId
      );
      if (employee) {
        const patch = employee.toPatch("remove");
        safetyAudit.removeEmployee(removeEmployeeRowId);
        if (emitPatch) emitPatch(patch);
      }
      api.bump();
    },

    updateEmployee(rowId, selectedEmployee, emitPatch) {
      const safetyAudit = api.getCurrentSafetyAudit();
      safetyAudit.updateEmployee(rowId, selectedEmployee);
      // Find the updated employee and create patch
      const employee = safetyAudit.employees.find((emp) => emp.id === rowId);
      if (employee) {
        const patch = employee.toPatch("update", {
          employee_id: employee.employee_id,
        });
        if (emitPatch) emitPatch(patch);
      }
      api.bump();
    },

    // Tab navigation action
    setActiveTab(tab) {
      api.setSafetyAuditFormUI({ activeTab: tab });
      // Lazy-load JSA data when JSA tab is opened
      if (tab === "jsa") {
        this.fetchJsaData();
      }
    },

    // --- DEL Association Actions ---

    showDelSearch() {
      api.setSafetyAuditFormUI({ activeTab: "findDel" });
    },

    cancelDelSearch() {
      api.setSafetyAuditFormUI({
        delSearchResults: [],
        delSearchTotalCount: 0,
        delSearchCurrentPage: 1,
        delSearchFilters: filtersInitialState({}),
        delSearchFilterPhaseList: [],
        delSearchShowFilterMenu: false,
        delSearchSelectedDelId: null,
        activeTab: "form",
      });
    },

    // Filter management actions for DEL search
    updateDelSearchFilter(filter) {
      const s = get();
      const prev = s[tabKey].safetyAuditForm.safetyAuditFormUI;
      api.setSafetyAuditFormUI({
        delSearchFilters: { ...prev.delSearchFilters, ...filter },
      });
    },

    clearDelSearchFilters() {
      api.setSafetyAuditFormUI({
        delSearchFilters: filtersInitialState({}),
        delSearchFilterPhaseList: [],
      });
    },

    setDelSearchFilterPhaseList(list) {
      api.setSafetyAuditFormUI({ delSearchFilterPhaseList: list });
    },

    toggleDelSearchFilterMenu() {
      const s = get();
      const prev = s[tabKey].safetyAuditForm.safetyAuditFormUI;
      api.setSafetyAuditFormUI({
        delSearchShowFilterMenu: !prev.delSearchShowFilterMenu,
      });
    },

    closeDelSearchFilterMenu() {
      api.setSafetyAuditFormUI({ delSearchShowFilterMenu: false });
    },

    async fetchDelSearchResults(page, pageSize) {
      api.setSafetyAuditFormUI({ delSearchLoading: true });
      try {
        const s = get();
        const ui = s[tabKey].safetyAuditForm.safetyAuditFormUI;
        const filters = ui.delSearchFilters;
        const p = page || ui.delSearchCurrentPage;
        const ps = pageSize || ui.delSearchPageSize;
        const result = await getDelLists(null, "reports", p, ps, filters);
        api.setSafetyAuditFormUI({
          delSearchResults: result.del_list || [],
          delSearchTotalCount: result.total_count || 0,
          delSearchCurrentPage: p,
          delSearchPageSize: ps,
          delSearchLoading: false,
        });
      } catch (error) {
        api.setSafetyAuditFormUI({ delSearchLoading: false });
        throw error;
      }
    },

    selectDelSearchResult(delId) {
      api.setSafetyAuditFormUI({ delSearchSelectedDelId: delId });
    },

    async handleAssociateDel(showLoading, hideLoading, showNotification) {
      const state = get();
      const tab = state[tabKey];
      const safetyAuditId = tab.currentSafetyAudit?.id;
      const delId = tab.safetyAuditForm.safetyAuditFormUI.delSearchSelectedDelId;

      if (!safetyAuditId || !delId) return;

      showLoading("Associating DEL...");
      try {
        const result = await associateDelWithSafetyAudit(safetyAuditId, delId);

        if (result.success) {
          const safetyAudit = api.getCurrentSafetyAudit();
          safetyAudit.applyDelAssociation(result);
          api.setSafetyAuditFormUI({
            delSearchResults: [],
            delSearchTotalCount: 0,
            delSearchFilters: filtersInitialState({}),
            delSearchFilterPhaseList: [],
            delSearchShowFilterMenu: false,
            delSearchSelectedDelId: null,
            activeTab: "form",
          });
          api.bump();
          hideLoading();
          showNotification({
            type: "success",
            title: "DEL Associated",
            message: "DEL has been successfully linked to this safety audit.",
          });
        } else {
          hideLoading();
          showNotification({
            type: "error",
            title: "Association Failed",
            message: result.error || "Failed to associate DEL.",
          });
        }
      } catch (error) {
        hideLoading();
        showNotification({
          type: "error",
          title: "Association Error",
          message: "An error occurred while associating the DEL.",
        });
      }
    },

    async handleDisassociateDel(showLoading, hideLoading, showNotification) {
      const state = get();
      const safetyAuditId = state[tabKey].currentSafetyAudit?.id;

      if (!safetyAuditId) return;

      showLoading("Removing DEL association...");
      try {
        const result = await disassociateDelFromSafetyAudit(safetyAuditId);

        if (result.success) {
          const safetyAudit = api.getCurrentSafetyAudit();
          safetyAudit.clearDelAssociation();
          api.setSafetyAuditFormUI({ activeTab: "form" });
          api.bump();
          hideLoading();
          showNotification({
            type: "success",
            title: "DEL Removed",
            message: "DEL association has been removed.",
          });
        } else {
          hideLoading();
          showNotification({
            type: "error",
            title: "Error",
            message: result.error || "Failed to remove DEL association.",
          });
        }
      } catch (error) {
        hideLoading();
        showNotification({
          type: "error",
          title: "Error",
          message: "An error occurred while removing the DEL association.",
        });
      }
    },

    async fetchJsaData() {
      const state = get();
      const currentAudit = state[tabKey].currentSafetyAudit;

      // Don't fetch if already loaded or no JSA associated
      if (!currentAudit || currentAudit.jsaLoaded || !currentAudit.jsa_id) return;

      api.setSafetyAuditFormUI({ jsaLoading: true });

      try {
        const result = await getSafetyAuditJsa(currentAudit.id);

        if (result.success) {
          const safetyAudit = api.getCurrentSafetyAudit();
          safetyAudit.setJsaData(result.jsa, result.jsa_version);
          api.setSafetyAuditFormUI({ jsaLoading: false });
          api.bump();
        }
      } catch (error) {
        api.setSafetyAuditFormUI({ jsaLoading: false });
      }
    },

    // JSA actions - same factory used by the DEL form, parameterized with
    // "safetyAuditForm" so setJsaUi reads/writes jsaFormUI from the correct
    // form slice. JSA is read-only in the safety audit (emitPatch is noOp),
    // but real actions are needed for UI toggles (section visibility, tab switching).
    jsa: makeJsaActions(set, get, tabKey, "safetyAuditForm"),

    // Photos actions using factory
    photos: makePhotosActions({
      getFormData: () => api.getCurrentSafetyAudit(),
      setPhotosUi: api.setPhotosUi,
      getPhotosUi: api.getPhotosUi,
      bump: () => api.bump(),
    }),

    // Autosave errors actions for WebSocket validation error handling
    autosaveErrors: makeSafetyAuditAutosaveErrorsActions(set, get, tabKey),
  });
};
